import { alpha } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid-pro';
import { omit } from 'lodash';

export const DataGridStyles = /** @type {const} */ ({
  border: 'none',
  [`.${gridClasses.row}.Mui-selected`]: {
    backgroundColor: 'unset'
  },
  [`.${gridClasses.cell}--selected`]: {
    backgroundColor: () => '#0288D180'
  },
  [`.${gridClasses.cell}--success`]: {
    backgroundColor: theme => alpha(theme.palette.success.main, 0.5)
  },
  [`.${gridClasses.cell}--error, .${gridClasses.cell}--error.${gridClasses.cell}--editing.${gridClasses.cell}.${gridClasses.cell}--textLeft.${gridClasses.cell}--editable`]: {
    backgroundColor: theme => alpha(theme.palette.error.main, 0.12),
    border: '2px solid',
    borderColor: 'error.main',
    [`&.${gridClasses['cell--editing']}`]: {
      background: theme => alpha(theme.palette.error.main, 0.12)
    }
  },
  [`.${gridClasses.cell}--edited, .${gridClasses.cell}.editable-date-cell, .${gridClasses.cell}.${gridClasses['cell--editing']}`]: {
    boxShadow: 'unset',
    border: '2px solid',
    borderColor: theme => theme.palette.primary.main,
    backgroundColor: theme => alpha(theme.palette.info.dark, theme.palette.mode === 'light' ? 0.04 : 0.2),
    input: {
      fontSize: 14,
      padding: '14.5px 9px'
    },
    fieldset: {
      border: 'unset'
    }
  },
  [`.${gridClasses.columnHeader}`]: {
    backgroundColor: 'misc.dataGridHeader'
  }
});

/**
 * Cell class added by getCellClassName when a wrap-cell is entering edit mode, so the padding
 * rules below can be skipped for it. Computed from the same rowModesModel the row's editable cells
 * read, so it lands in the same render as their view-to-edit swap: MUI's own `cell--editing` class
 * is only added a render later (from an internal effect reacting to the rowModesModel prop), which
 * caused a brief tall-then-shrink flash when entering edit mode.
 */
export const WRAP_CELL_EDITING_CLASS = 'wrap-cell--editing';

/**
 * Appends WRAP_CELL_EDITING_CLASS to a getCellClassName result when the cell is entering edit mode.
 *
 * @param {string} className - the class name(s) already computed for the cell
 * @param {boolean} isEnteringEditMode - whether the cell's row is switching into edit mode this render
 * @returns {string} - className with WRAP_CELL_EDITING_CLASS appended when relevant
 */
export const appendWrapCellEditingClassName = (className, isEnteringEditMode) => [className, isEnteringEditMode && WRAP_CELL_EDITING_CLASS].filter(Boolean).join(' ');

// Wrapping cells (cellClassName: 'wrap-cell') need whitespace/word-break plus extra vertical padding
// to breathe when their text wraps to multiple lines. Restored only on non-editing wrap-cells: with
// getRowHeight 'auto', other cells stay tight, and editing cells already size themselves via their
// own input, so this padding would otherwise stack on top of it and inflate the row even when nothing wraps.
export const wrapCellStyles = /** @type {const} */ ({
  '& .wrap-cell': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.3
  },
  [`&.MuiDataGrid-root--densityCompact .wrap-cell:not(.${WRAP_CELL_EDITING_CLASS})`]: { py: 1 },
  [`&.MuiDataGrid-root--densityStandard .wrap-cell:not(.${WRAP_CELL_EDITING_CLASS})`]: { py: 1.875 },
  [`&.MuiDataGrid-root--densityComfortable .wrap-cell:not(.${WRAP_CELL_EDITING_CLASS})`]: { py: 2.75 }
});

export const DataGridStylesWithoutSelectionOverride = omit(
  {
    ...DataGridStyles,
    [`.MuiDataGrid-selectedRowCount`]: { visibility: 'hidden', fontSize: 0 },
    ['.MuiDataGrid-cell:focus']: { outline: 'none' },
    ['.MuiDataGrid-cell:focus-within']: { outline: 'none' },
    ['.MuiDataGrid-columnHeader:focus']: { outline: 'none' },
    ['.MuiDataGrid-columnHeader:focus-within']: { outline: 'none' }
  },
  [`.${gridClasses.row}.Mui-selected`, `.${gridClasses.cell}--selected`]
);
